import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  getProjectGitHubCopilotBudgets,
  setGitHubCopilotBudget,
  getUserGitHubCopilotBudgets,
  getUserGitHubCopilotUsage,
} from "../../api/githubCopilotBudgetsHttpClient";
import {
  GitHubCopilotBudgetMember,
  GitHubCopilotUserBudgets,
  GitHubCopilotUsage,
} from "../../api/models/GitHubCopilotBudget";

const MIN_BUDGET = 0;
const MAX_BUDGET = 2000;
const BUDGET_RANGE_ERROR = "Budget must be between 0 and 2000";

interface ExpandedUserData {
  budgets: GitHubCopilotUserBudgets | null;
  usage: GitHubCopilotUsage | null;
  loading: boolean;
}

interface UseGitHubCopilotBudgetsModalArgs {
  open: boolean;
  projectId: string;
}

const createDefaultExpandedData = (): ExpandedUserData => ({
  budgets: { baseline: 35, projectBudgets: [] },
  usage: { currentUsage: 0 },
  loading: false,
});

const validateBudgetInput = (value: string) => {
  if (value.trim() === "") {
    return "Budget is required";
  }

  const parsedValue = parseFloat(value);
  if (isNaN(parsedValue)) {
    return "Enter a valid budget amount";
  }

  if (parsedValue < MIN_BUDGET || parsedValue > MAX_BUDGET) {
    return BUDGET_RANGE_ERROR;
  }

  return "";
};

export const useGitHubCopilotBudgetsModal = ({
  open,
  projectId,
}: UseGitHubCopilotBudgetsModalArgs) => {
  const { enqueueSnackbar } = useSnackbar();
  const [members, setMembers] = useState<GitHubCopilotBudgetMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [expandedData, setExpandedData] = useState<Record<string, ExpandedUserData>>({});
  const [budgetInputs, setBudgetInputs] = useState<Record<string, string>>({});
  const [budgetErrors, setBudgetErrors] = useState<Record<string, string>>({});
  const [savingMember, setSavingMember] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProjectGitHubCopilotBudgets(projectId);
      setMembers(data);
      const inputs: Record<string, string> = {};
      data.forEach((member) => {
        inputs[member.memberId] = member.budget.toString();
      });
      setBudgetInputs(inputs);
      setBudgetErrors({});
    } catch {
      enqueueSnackbar("Failed to load GitHub Copilot budgets", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [projectId, enqueueSnackbar]);

  useEffect(() => {
    if (open) {
      fetchMembers();
      setExpandedRow(null);
      setExpandedData({});
      setBudgetErrors({});
    }
  }, [open, fetchMembers]);

  const handleToggleRow = useCallback(
    async (memberId: string) => {
      if (expandedRow === memberId) {
        setExpandedRow(null);
        return;
      }

      setExpandedRow(memberId);

      if (!expandedData[memberId]) {
        setExpandedData((prev) => ({
          ...prev,
          [memberId]: { budgets: null, usage: null, loading: true },
        }));

        try {
          const [budgets, usage] = await Promise.all([
            getUserGitHubCopilotBudgets(memberId),
            getUserGitHubCopilotUsage(memberId),
          ]);

          setExpandedData((prev) => ({
            ...prev,
            [memberId]: { budgets, usage, loading: false },
          }));
        } catch {
          setExpandedData((prev) => ({
            ...prev,
            [memberId]: createDefaultExpandedData(),
          }));
        }
      }
    },
    [expandedData, expandedRow],
  );

  const handleBudgetChange = useCallback((memberId: string, value: string) => {
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setBudgetInputs((prev) => ({ ...prev, [memberId]: value }));
      setBudgetErrors((prev) => {
        const nextErrors = { ...prev };
        const validationError = validateBudgetInput(value);

        if (validationError === "" || value === "") {
          delete nextErrors[memberId];
        } else {
          nextErrors[memberId] = validationError;
        }

        return nextErrors;
      });
    }
  }, []);

  const handleSave = useCallback(
    async (memberId: string) => {
      const inputValue = budgetInputs[memberId] ?? "";
      const validationError = validateBudgetInput(inputValue);

      if (validationError !== "") {
        setBudgetErrors((prev) => ({ ...prev, [memberId]: validationError }));
        enqueueSnackbar(validationError, {
          variant: "error",
        });
        return;
      }

      const budgetValue = parseFloat(inputValue);

      const userData = expandedData[memberId];
      if (userData?.usage && userData?.budgets) {
        const currentUsage = userData.usage.currentUsage;
        const otherProjectBudgets = userData.budgets.projectBudgets
          .filter((projectBudget) => projectBudget.projectId !== projectId)
          .reduce((sum, projectBudget) => sum + projectBudget.budget, 0);
        const newTotalBudget =
          userData.budgets.baseline + otherProjectBudgets + budgetValue;

        if (newTotalBudget < currentUsage) {
          enqueueSnackbar(
            `Cannot reduce budget below current AI Credits usage ($${currentUsage.toFixed(
              2,
            )}). Usage resets on the 1st of each month.`,
            { variant: "error", autoHideDuration: 6000 },
          );
          return;
        }
      }

      setSavingMember(memberId);
      try {
        await setGitHubCopilotBudget(projectId, {
          memberId,
          budget: budgetValue,
        });

        setBudgetErrors((prev) => {
          const nextErrors = { ...prev };
          delete nextErrors[memberId];
          return nextErrors;
        });
        enqueueSnackbar("Budget saved successfully", { variant: "success" });

        await fetchMembers();

        if (expandedRow === memberId) {
          const [budgets, usage] = await Promise.all([
            getUserGitHubCopilotBudgets(memberId),
            getUserGitHubCopilotUsage(memberId),
          ]);

          setExpandedData((prev) => ({
            ...prev,
            [memberId]: { budgets, usage, loading: false },
          }));
        }
      } catch {
        enqueueSnackbar("Failed to save budget", { variant: "error" });
      } finally {
        setSavingMember(null);
      }
    },
    [budgetInputs, enqueueSnackbar, expandedData, expandedRow, fetchMembers, projectId],
  );

  return {
    members,
    loading,
    expandedRow,
    expandedData,
    budgetInputs,
    budgetErrors,
    savingMember,
    handleToggleRow,
    handleBudgetChange,
    handleSave,
  };
};
